import { useCallback, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { useNetwork } from 'hooks';
import {
	IsKycLoading,
	kycSettingsState,
	whiteLableLogoState,
} from 'global-stores';
import { API_URL } from 'constant';
import {
	ITimezoneOption,
	TIMEZONE_OPTIONS,
	timezoneState,
} from 'global-stores/timezone';

export const useGeneralSettings = () => {
	// global states
	const [SettingsKyc, setKycSettings] = useRecoilState(kycSettingsState);
	const setIsLogoLoaded = useSetRecoilState(IsKycLoading);
	const setIsLogoLoading = useSetRecoilState(whiteLableLogoState);
	const setTimezone = useSetRecoilState(timezoneState);

	// local state
	const [settingsLoaded, setSettingsLoaded] = useState(true);

	// hooks
	const { get: getSettings } = useNetwork();

	// Helper function to sync timezone across browsers
	const syncTimezone = useCallback((timeZone: string) => {
		const timezoneOption = TIMEZONE_OPTIONS.find(
			(option: ITimezoneOption) =>
				option.value.toLowerCase() === timeZone.toLowerCase()
		);

		const newTimezoneState = {
			timezone: timezoneOption?.value ?? '',
			displayName: timezoneOption?.label ?? '',
			abbreviation: timezoneOption?.abbreviation ?? '',
		};

		setTimezone(newTimezoneState);
	}, [setTimezone]);

	// Helper function to update client settings
	const updateClientSettings = useCallback((clientData: any) => {
		const {
			name, logo, whitelistedDomains, _id, settings,
			whitelabel, whitelistConfig, whitelabelKey,
			showPricing, loaderWhitelabel, loader
		} = clientData;

		if (name !== '') setKycSettings(prev => ({ ...prev, name }));
		if (logo !== '') setKycSettings(prev => ({ ...prev, logo }));
		if (whitelistedDomains) setKycSettings(prev => ({ ...prev, whitelistedDomains }));
		if (_id !== '') setKycSettings(prev => ({ ...prev, _id }));
		if (settings) setKycSettings(prev => ({ ...prev, settings }));
		if (whitelabel) setKycSettings(prev => ({ ...prev, whitelabel }));
		if (whitelabelKey) setKycSettings(prev => ({ ...prev, whitelabelKey }));
		if (whitelistConfig) setKycSettings(prev => ({ ...prev, whitelistConfig }));

		setKycSettings(prev => ({
			...prev,
			showPricing,
			loaderWhitelabel: !!loaderWhitelabel,
			loader,
		}));

		const { brandColor, bodyFont } = settings ?? {};
		localStorage.setItem('theme-color', brandColor ?? '#0051cc');
		localStorage.setItem('theme-font', bodyFont?.family ?? 'Poppins');
	}, [setKycSettings]);

	// Main function
	const getClientDetails = useCallback(async () => {
		const isFirstLoad = SettingsKyc?._id?.length === 0;

		if (isFirstLoad) {
			setSettingsLoaded(false);
			setIsLogoLoading(true);
		}

		try {
			const response = await getSettings(`${API_URL.CLIENT_DETAILS}`);
			if (response?.message !== 'ok' || !response?.data?.[0]) return;

			const clientData = response.data[0];

			// Always sync timezone
			if (clientData.timeZone) {
				syncTimezone(clientData.timeZone);
			}

			// Always update client + theme settings (not just first load)
			updateClientSettings(clientData);

			if (isFirstLoad) {
				setIsLogoLoaded(false);
				setIsLogoLoading(false);
			}
		} catch {
			// silent fail
		} finally {
			if (isFirstLoad) {
				setSettingsLoaded(true);
				setIsLogoLoaded(false);
			}
		}
	}, [
		SettingsKyc?._id?.length,
		getSettings,
		syncTimezone,
		updateClientSettings,
		setSettingsLoaded,
		setIsLogoLoading,
		setIsLogoLoaded,
	]);

	return { getClientDetails, settingsLoaded };
};
